// SSR/SSG polyfill: Vuetify focus trap uses document.addEventListener/removeEventListener
// in onScopeDispose without SSR guards. Provide a minimal stub to prevent crashes.
if (typeof document === "undefined") {
  const noop = () => {};
  const noopEl = { setAttribute: noop, getAttribute: () => null, style: {} };
  globalThis.document = {
    addEventListener: noop,
    removeEventListener: noop,
    querySelector: () => null,
    querySelectorAll: () => [],
    createElement: () => ({ ...noopEl, appendChild: noop }),
    getElementById: () => null,
    head: { appendChild: noop },
    body: { appendChild: noop, style: {} },
    documentElement: { lang: "", setAttribute: noop },
    cookie: "",
    title: "",
    hidden: false,
  };
}

// Components
import App from "./App.vue";

// Variables CSS globales (z-index, transitions, utilities)
import "@/assets/styles/variables.css";

// Composables
import { ViteSSG } from "vite-ssg";
import { createI18n } from "vue-i18n";

// Pinia
import { createPinia } from "pinia";

// Router
import { routes } from "./router/index.js";

// Plugins
import { registerPlugins } from "@/plugins";

// Plugin de Google Analytics
import GoogleAnalytics from "./plugins/google-analytics";

// Variable global para i18n
let i18n;

// Promesa que se resuelve cuando i18n está listo
let i18nReadyResolve;
const i18nReady = new Promise((resolve) => {
  i18nReadyResolve = resolve;
});

// Idiomas soportados
const SUPPORTED_LOCALES = ["es", "en"];
const DEFAULT_LOCALE = "es";

/**
 * Detecta el idioma inicial basándose en:
 * 1. La URL actual (prioridad para SEO)
 * 2. localStorage (preferencia guardada)
 * 3. Idioma del navegador
 * 4. Fallback a español
 */
function detectInitialLocale(path) {
  // 1. Detectar por URL (importante para SEO - Google entra por /en/speed-test)
  if (path?.startsWith("/en")) {
    return "en";
  }

  // En SSG, no tenemos acceso a window durante el build
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  // 2. Preferencia guardada
  const savedLocale = localStorage.getItem("user-locale");
  if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
    return savedLocale;
  }

  // 3. Idioma del navegador
  const browserLang = navigator.language?.split("-")[0];
  if (browserLang && SUPPORTED_LOCALES.includes(browserLang)) {
    return browserLang;
  }

  // 4. Fallback
  return DEFAULT_LOCALE;
}

/**
 * Carga lazy de idiomas - solo carga el idioma necesario
 */
async function loadLocaleMessages(locale) {
  try {
    const messages = await import(`./locales/${locale}.json`);
    return messages.default || messages;
  } catch {
    if (locale !== DEFAULT_LOCALE) {
      return loadLocaleMessages(DEFAULT_LOCALE);
    }
    return {};
  }
}

/**
 * Función para cambiar de idioma dinámicamente
 * Exportada para usar desde componentes
 */
export async function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    return false;
  }

  // Si el idioma no está cargado, cargarlo
  if (!i18n.global.availableLocales.includes(locale)) {
    const messages = await loadLocaleMessages(locale);
    i18n.global.setLocaleMessage(locale, messages);
  }

  // Cambiar idioma
  i18n.global.locale.value = locale;

  // Guardar preferencia (solo en cliente)
  if (typeof window !== "undefined") {
    localStorage.setItem("user-locale", locale);
  }

  return true;
}

/**
 * Función helper para obtener i18n de forma segura
 * Espera a que esté inicializado antes de devolverlo
 */
export async function getI18n() {
  await i18nReady;
  return i18n;
}

// Rutas para prerendering
import { prerenderRoutes } from "./ssg-routes.js";

// Exportar la app usando ViteSSG
export const createApp = ViteSSG(
  App,
  {
    routes,
    base: "/",
  },
  async ({ app, isClient }) => {
    // Detectar idioma inicial
    const path = isClient ? window.location.pathname : "/";
    const initialLocale = detectInitialLocale(path);

    // Cargar idiomas: en SSG cargamos todos (pre-renderiza ambos), en cliente solo el inicial
    const initialMessages = await loadLocaleMessages(initialLocale);
    const allMessages = { [initialLocale]: initialMessages };

    if (!isClient) {
      // SSG: cargar todos los idiomas para que useHead/t() funcione en ambas locales
      for (const loc of SUPPORTED_LOCALES) {
        if (loc !== initialLocale) {
          allMessages[loc] = await loadLocaleMessages(loc);
        }
      }
    }

    // Crear instancia de i18n
    i18n = createI18n({
      locale: initialLocale,
      legacy: false,
      fallbackLocale: DEFAULT_LOCALE,
      messages: allMessages,
      silentFallbackWarn: true,
      silentTranslationWarn: true,
      // Controlamos nosotros el HTML del JSON, no viene de input del usuario.
      // Silenciar el warning "Detected HTML in ... message" que ensucia consola
      // en FAQ, Prensa y otras páginas con texto enriquecido intencionado.
      warnHtmlInMessage: "off",
      warnHtmlMessage: false,
    });

    // Marcar i18n como listo
    i18nReadyResolve(i18n);

    // Crear Pinia
    const pinia = createPinia();

    // Registrar plugins
    registerPlugins(app);

    app.use(i18n);
    app.use(pinia);

    // OpenLayers solo en cliente (tiene dependencias de DOM)
    if (isClient) {
      app.use(GoogleAnalytics);

      // Initialize Web Vitals tracking (no bloquea)
      import("./utils/webVitals.js").then(({ initWebVitals }) => {
        initWebVitals();
      });

      // lang del HTML se gestiona via Unhead (useSeoHead: htmlAttrs.lang)

      // Precargar el otro idioma en idle time (no bloquea)
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => {
          const otherLocales = SUPPORTED_LOCALES.filter(
            (l) => l !== initialLocale
          );
          otherLocales.forEach(async (locale) => {
            const messages = await loadLocaleMessages(locale);
            i18n.global.setLocaleMessage(locale, messages);
          });
        });
      } else {
        // Fallback para Safari
        setTimeout(() => {
          const otherLocales = SUPPORTED_LOCALES.filter(
            (l) => l !== initialLocale
          );
          otherLocales.forEach(async (locale) => {
            const messages = await loadLocaleMessages(locale);
            i18n.global.setLocaleMessage(locale, messages);
          });
        }, 2000);
      }
    }
  },
  {
    // Rutas a prerrenderizar
    includedRoutes: () => prerenderRoutes,
  }
);

// Exportar para uso en otros módulos.
// `i18n` no se exporta directamente porque es un `let` mutable; los
// consumidores deben usar `getI18n()` que espera al `i18nReady` antes
// de devolverlo, evitando el problema de exportar bindings mutables.
export { i18nReady, SUPPORTED_LOCALES, DEFAULT_LOCALE };
